import { FormContext } from "@common/contexts/Form";
import { FormStyleContext } from "@common/contexts/FormStyle";
import { TourContext } from "@common/contexts/Tour";
import Button, { type ButtonProps } from "@common/controls/Button/component";
import { useI18n } from "@common/hooks/i18n";
import toasts from "@common/lib/toast";
import classnames from "classnames";
import React from "react";
import { GetFinalValuesError } from "../lib";
import TourSubmitEvent from "./TourSubmitEvent";

export type SubmitButtonProps = Omit<ButtonProps, "children"> & {
  children?: any;
  shouldTrackChanges?: boolean;
  validate?: boolean;
  tourStep?: string;
};

/**
 * Can be used anywhere inside a controlled form.
 * Always triggers form validation.
 * onClick overrides form submit.
 */
const SubmitButton = ({ children, className, onClick, isDisabled, icon, ...props }: SubmitButtonProps) => {
  const i18n = useI18n();

  const context = React.useContext(FormContext);
  const { primaryButtonStyle } = React.useContext(FormStyleContext);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const tourContext = React.useContext(TourContext);

  const _onClick = React.useCallback(
    async (e: React.MouseEvent) => {
      if (!onClick && !context) return;

      setIsSubmitting(true);

      try {
        if (context) {
          const isValid = await context.validate();
          if (!isValid) return;
        }
        const r = onClick ? onClick!(e) : context.submit();
        if (r instanceof Promise) {
          const res = await r;
          if (context.options.toast) {
            toasts.info(context.options.toast);
          }

          if (props.tourStep) TourSubmitEvent.dispatch({ tourStep: props.tourStep });

          return res;
        }

        if (props.tourStep) TourSubmitEvent.dispatch({ tourStep: props.tourStep });

        return r;
      } catch (e) {
        if (!(e instanceof GetFinalValuesError)) {
          // TODO: better solution
          toasts.error(e.message ?? e);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [context.submit, onClick, context.options.toast, props.tourStep]
  );

  const style = React.useMemo(
    () => ({
      ...primaryButtonStyle,
      ...props.style
    }),
    [primaryButtonStyle, props.style]
  );

  return (
    <Button
      {...props}
      className={classnames(className, "btn_submit", {
        btn_styled: primaryButtonStyle
      })}
      tourStep={props.tourStep}
      icon={icon}
      onClick={_onClick}
      isDisabled={
        isSubmitting ||
        isDisabled ||
        (!!props.tourStep && !!tourContext?.tour && !tourContext.getIsActiveStep(props.tourStep))
      }
      style={style}
      type={props.type ?? "primary"}
    >
      {children ?? props.title ?? i18n.save}
    </Button>
  );
};
export default SubmitButton;
